import { supabase } from './supabase';
import { trackEvent } from './analytics';
import { isSubscriptionManagementResponse } from './billingRecovery';

export type CheckoutLink = { url: string; preserveInNewTab: boolean; kind?: 'billing_management' };
const RECOVERY_TIMEOUT_MS = 2500;
const CHECKOUT_TIMEOUT_MS = 30_000;

async function prepareRecovery(source: string): Promise<{ work?: string; preserveInNewTab: boolean }> {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    const work = await Promise.race([
      import('./checkoutDraft').then(({ prepareCheckoutDraft }) => prepareCheckoutDraft(source)),
      new Promise<never>((_, reject) => {
        timeout = setTimeout(() => reject(new Error('Recovery preparation timed out')), RECOVERY_TIMEOUT_MS);
      }),
    ]);
    return { work, preserveInNewTab: false };
  } catch (error) {
    // Keep the working editor alive in its original tab if local storage is
    // blocked, full or slow. Recovery must not prevent session creation.
    trackEvent('checkout_recovery_unavailable', { source, reason: error instanceof Error && error.message === 'Recovery preparation timed out' ? 'timeout' : 'storage_error' });
    return { preserveInNewTab: true };
  } finally {
    clearTimeout(timeout);
  }
}

export async function requestCheckout(endpoint: string, body: Record<string, unknown>): Promise<CheckoutLink | null> {
  const source = typeof body.source === 'string' ? body.source : 'unknown';
  const controller = new AbortController();
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let stage = 'session';
  const startedAt = Date.now();
  try {
    return await Promise.race([
      (async (): Promise<CheckoutLink> => {
        const { data: { session } } = await supabase.auth.getSession();
        if (controller.signal.aborted) throw new Error('Checkout timed out');
        const userId = session?.user?.id;
        const assertSameAccount = async () => {
          const { data: { session: current } } = await supabase.auth.getSession();
          if (controller.signal.aborted || current?.user?.id !== userId) throw new Error('Checkout account changed');
        };
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
        stage = 'save_work';
        const recovery = await prepareRecovery(source);
        await assertSameAccount();
        stage = 'request';
        const response = await fetch(endpoint, {
          method: 'POST', signal: controller.signal, headers,
          body: JSON.stringify({ ...body, ...(recovery.work ? { work: recovery.work } : {}) }),
        });
        const result = await response.json();
        await assertSameAccount();
        if (response.status === 409 && endpoint === '/api/stripe/create-checkout-session'
          && userId && session?.access_token && isSubscriptionManagementResponse(result)) {
          trackEvent('checkout_billing_management_required', { source, reason: result.reason, status: result.status });
          // Never follow a customer ID or URL supplied in an error payload.
          // The existing page authenticates again and opens the full portal.
          // Keep editor work in its tab: the portal return has no draft token.
          return { url: '/billing', preserveInNewTab: true, kind: 'billing_management' };
        }
        if (!response.ok) throw new Error('Checkout request failed');
        if (!result || typeof result.url !== 'string' || !result.url) throw new Error('Checkout URL missing');
        return { url: result.url, preserveInNewTab: recovery.preserveInNewTab };
      })(),
      new Promise<never>((_, reject) => {
        timeout = setTimeout(() => { controller.abort(); reject(new Error('Checkout timed out')); }, CHECKOUT_TIMEOUT_MS);
      }),
    ]);
  } catch {
    trackEvent('checkout_preparation_failed', { stage, source, reason: controller.signal.aborted ? 'timeout' : 'error', elapsed_ms: Date.now() - startedAt });
    return null;
  } finally {
    clearTimeout(timeout);
  }
}
